import {useDebounce} from '@github-ui/use-debounce'
import {useQuery} from '@tanstack/react-query'
import {createContext, memo, use, useCallback, useMemo, useState} from 'react'

import {apiCountMatchedRepositoryItems} from '../../api/repository/api-count-matched-repository-items'
import type {CountMatchedRepositoryItemsRequest} from '../../api/repository/contracts'
import {useProjectNumber} from '../memex/use-project-number'

export type AutoAddItemCountContextType = {
  /**
   * The count of items that would be auto-added from the attached repository
   */
  autoAddItemCount: number

  /**
   * Function to refresh the auto-add item count for a specific repository and query
   */
  refreshAutoAddItemCount: ({repositoryId, query}: {repositoryId?: number; query: string}) => void

  /**
   * Function to reset the auto-add item count to 0
   */
  resetAutoAddItemCount: () => void
}

const AutoAddItemCountContext = createContext<AutoAddItemCountContextType | null>(null)

export const AutoAddItemCountProvider = memo(function AutoAddItemCountProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const {projectNumber} = useProjectNumber()
  const [queryParams, setQueryParams] = useState<CountMatchedRepositoryItemsRequest | null>(null)

  const {data, isError} = useQuery({
    queryKey: ['autoAddItemCount', queryParams],
    queryFn: () => (queryParams ? apiCountMatchedRepositoryItems(queryParams) : Promise.resolve(null)),
    enabled: queryParams !== null,
    retry: false,
  })

  const autoAddItemCount = isError ? 0 : (data?.count ?? 0)

  const refreshAutoAddItemCount = useCallback(
    ({repositoryId, query}: {repositoryId?: number; query: string}) => {
      const params: CountMatchedRepositoryItemsRequest = {
        query,
        memexNumber: projectNumber,
      }
      if (repositoryId) {
        params.repositoryId = repositoryId
      }

      setQueryParams(params)
    },
    [projectNumber],
  )

  const resetAutoAddItemCount = useCallback(() => {
    setQueryParams(null)
  }, [])

  const debouncedRefreshAutoAddItemCount = useDebounce(refreshAutoAddItemCount, 500, {trailing: true, leading: false})

  const contextValue = useMemo(
    () => ({
      autoAddItemCount,
      refreshAutoAddItemCount: debouncedRefreshAutoAddItemCount,
      resetAutoAddItemCount,
    }),
    [autoAddItemCount, debouncedRefreshAutoAddItemCount, resetAutoAddItemCount],
  )

  return <AutoAddItemCountContext value={contextValue}>{children}</AutoAddItemCountContext>
})

export const useAutoAddItemCount = (): AutoAddItemCountContextType => {
  const contextValue = use(AutoAddItemCountContext)

  if (!contextValue) {
    throw new Error('useAutoAddItemCount must be used within an AutoAddItemCountProvider')
  }

  return contextValue
}
